import React, { useState } from 'react';
import axios from 'axios';
import { formatApiError } from '../lib/errors';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Mail, AtSign } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChangeEmailSection({ currentEmail, onChanged }) {
  const { language } = useLanguage();
  const isLV = language === 'lv';

  const [newEmail, setNewEmail] = useState('');
  const [currentPw, setCurrentPw] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    const target = newEmail.trim().toLowerCase();
    if (!target || !target.includes('@')) {
      setError(isLV ? 'Nederīgs e-pasts' : 'Invalid email');
      return;
    }
    if (target === (currentEmail || '').toLowerCase()) {
      setError(isLV ? 'Šis ir pašreizējais e-pasts' : 'Same as current email');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/change-email`, {
        new_email: target,
        current_password: currentPw,
      });
      setSuccess(true);
      setNewEmail('');
      setCurrentPw('');
      if (onChanged) onChanged(res.data.email);
      setTimeout(() => setSuccess(false), 3500);
    } catch (err) {
      setError(formatApiError(err, (isLV ? 'Kļūda. Mēģini vēlreiz.' : 'Error. Try again.')));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white border border-[#E5E4E0] rounded-2xl p-6" data-testid="change-email-section">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-[#D97736]/10 flex items-center justify-center">
          <AtSign className="w-5 h-5 text-[#D97736]" />
        </div>
        <div>
          <h3 className="font-outfit font-semibold text-[#121212]">
            {isLV ? 'Mainīt e-pastu' : 'Change email'}
          </h3>
          <p className="text-xs text-[#5C5C5C]">
            {isLV
              ? `Pašreizējais: ${currentEmail}`
              : `Current: ${currentEmail}`}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3">
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm" data-testid="change-email-error">
            {error}
          </div>
        )}
        {success && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-xl text-green-700 text-sm" data-testid="change-email-success">
            {isLV ? 'E-pasts veiksmīgi nomainīts' : 'Email successfully changed'}
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="new_email" className="text-sm text-[#121212]">
            {isLV ? 'Jaunais e-pasts' : 'New email'}
          </Label>
          <div className="relative">
            <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8A8A]" />
            <Input
              id="new_email"
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              required
              className="h-11 bg-[#FAFAFA] border-[#E5E4E0] pl-9"
              placeholder="jauns@epasts.lv"
              data-testid="new-email-input"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="current_pw_email" className="text-sm text-[#121212]">
            {isLV ? 'Apstiprini ar pašreizējo paroli' : 'Confirm with current password'}
          </Label>
          <Input
            id="current_pw_email"
            type="password"
            value={currentPw}
            onChange={(e) => setCurrentPw(e.target.value)}
            required
            autoComplete="current-password"
            className="h-11 bg-[#FAFAFA] border-[#E5E4E0]"
            data-testid="change-email-password-input"
          />
        </div>

        <Button
          type="submit"
          disabled={loading || !newEmail || !currentPw}
          className="w-full h-11 bg-[#D97736] hover:bg-[#C06528] text-white"
          data-testid="change-email-submit-btn"
        >
          {loading
            ? (isLV ? 'Maina...' : 'Changing...')
            : (isLV ? 'Nomainīt e-pastu' : 'Change email')}
        </Button>
      </form>
    </div>
  );
}
